import irmHeader from 'assets/images/irm-header.png';

// ── Shared helpers (duplicated per repo convention — each print file owns these) ──

function amountInWords(amount) {
  if (!amount && amount !== 0) return '';
  const n = Math.abs(Math.round(amount * 100));
  const paise = n % 100; const rupees = Math.floor(n / 100);
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const toW = (num) => {
    if (num === 0) return '';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? ' '+ones[num%10] : '');
    return ones[Math.floor(num/100)] + ' Hundred' + (num%100 ? ' '+toW(num%100) : '');
  };
  const inIndian = (num) => {
    if (num === 0) return 'Zero'; let out = '';
    if (num >= 10000000) { out += toW(Math.floor(num/10000000))+' Crore '; num %= 10000000; }
    if (num >= 100000)   { out += toW(Math.floor(num/100000))  +' Lakh ';  num %= 100000; }
    if (num >= 1000)     { out += toW(Math.floor(num/1000))    +' Thousand '; num %= 1000; }
    if (num > 0)         { out += toW(num); } return out.trim();
  };
  let out = inIndian(rupees);
  if (paise > 0) out += ` and ${toW(paise)} Paise`;
  return 'INR ' + out.trim();
}

const MODE_STYLE = {
  CASH:         { bg: '#e8f5e9', color: '#2e7d32' },
  UPI:          { bg: '#e3f2fd', color: '#1565c0' },
  NEFT:         { bg: '#e3f2fd', color: '#1565c0' },
  RTGS:         { bg: '#e3f2fd', color: '#1565c0' },
  CHEQUE:       { bg: '#fce4ec', color: '#ad1457' },
  DEMAND_DRAFT: { bg: '#fce4ec', color: '#ad1457' },
};
const STATUS_STYLE = {
  PAID: 'background:#e8f5e9;color:#2e7d32',
  CANCELLED: 'background:#fdecea;color:#c62828',
};

/**
 * Print a Supplier Payment Voucher — branded to match the rest of the
 * pharmacy print family (irmHeader logo, Montserrat title, #b2d8de bordered
 * letterhead, A5 portrait, PAN/bank/seal footer).
 * txn     : the specific payment transaction being printed (amount, payment_mode,
 *           payment_date, utr_number, notes, status)
 * invoice : the GRN — carries `.supplier`, `.inward_no`, `.received_date`,
 *           `.net_amount`, `.payment` (amount_due/amount_paid/payment_status/
 *           due_date/next_payment_date)
 * allTxns : every payment transaction against this GRN (for the payment
 *           schedule/history table — surfaces prior payments if any exist)
 * ph      : selected pharmacy object (company/letterhead details)
 */
export default function printSupplierPayment(txn, invoice, allTxns, ph) {
  if (!txn || !invoice) return;
  const PRIMARY = '#00798E';
  const LIGHT_BG = '#e0f4f7';
  const BORDER = '#b2d8de';
  const sup = invoice.supplier || {};
  const pay = invoice.payment || {};
  const fmtD = (d) => { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const C = `border:1px solid ${BORDER};padding:3px 6px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;`;

  const modeStyle = MODE_STYLE[txn.payment_mode] || { bg: '#f5f5f5', color: '#757575' };
  const txns = (allTxns || []).slice().sort((a, b) => new Date(a.payment_date || 0) - new Date(b.payment_date || 0));
  const totalPaid = txns.filter(t => t.status !== 'CANCELLED').reduce((s, t) => s + Number(t.amount || 0), 0);
  const amountDue = Number(pay.amount_due || invoice.net_amount || 0);
  const balanceDue = Math.max(0, amountDue - totalPaid);

  const historyRows = txns.map((t, i) => {
    const isCurrent = t.id === txn.id;
    return `
    <tr style="${isCurrent ? `background:${LIGHT_BG}` : (i % 2 === 0 ? '' : 'background:#fafafa')}">
      <td style="${C}text-align:center">${i + 1}</td>
      <td style="${C}">${fmtD(t.payment_date)}</td>
      <td style="${C}text-align:center">${t.payment_mode || '—'}</td>
      <td style="${C}font-family:monospace">${t.utr_number || '—'}</td>
      <td style="${C}text-align:right;font-family:monospace;font-weight:${isCurrent ? 800 : 600}">${Number(t.amount).toFixed(2)}</td>
      <td style="${C}text-align:center"><span style="padding:1px 6px;border-radius:3px;font-size:6.5pt;font-weight:700;${STATUS_STYLE[t.status] || 'background:#eee;color:#555'}">${t.status || 'PAID'}${isCurrent ? ' (this)' : ''}</span></td>
    </tr>`;
  }).join('');

  const body = `
    <div style="margin-bottom:6px">
      <div style="line-height:0">
        <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
      </div>
      <div style="text-align:center;margin-top:5px">
        <div style="font-size:13pt;font-weight:700;letter-spacing:1px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">SUPPLIER PAYMENT VOUCHER</div>
      </div>
    </div>
    <table style="width:100%;border-collapse:collapse;border:1.5px solid ${BORDER}">
      <tr>
        <td style="width:50%;border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
          <div style="font-size:11pt;font-weight:900;color:${PRIMARY};line-height:1.1">${ph?.name || 'PHARMACY'}</div>
          ${ph?.sub_title ? `<div style="font-size:7.5pt;color:#444;margin-top:1px">${ph.sub_title}</div>` : ''}
          <div style="font-size:7pt;color:#333;margin-top:2px;line-height:1.5">${ph?.address || ''}${ph?.city ? ', '+ph.city : ''}${ph?.pincode ? ' - '+ph.pincode : ''}</div>
          ${ph?.dl_no ? `<div style="font-size:6.5pt;color:#555">DL No : ${ph.dl_no}</div>` : ''}
          ${ph?.gstin ? `<div style="font-size:6.5pt;color:#555">GSTIN : ${ph.gstin}</div>` : ''}
        </td>
        <td style="width:50%;padding:5px 8px;vertical-align:top">
          <table style="width:100%;border-collapse:collapse;font-size:7.5pt">
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">Payment Date</td><td style="font-weight:700;padding-bottom:2px">${fmtD(txn.payment_date)}</td></tr>
            <tr><td style="color:#777;padding-bottom:2px;padding-right:8px;white-space:nowrap">Mode</td><td style="padding-bottom:2px"><span style="padding:2px 8px;border-radius:3px;font-size:7pt;font-weight:700;background:${modeStyle.bg};color:${modeStyle.color}">${txn.payment_mode || '—'}</span></td></tr>
            ${txn.utr_number ? `<tr><td style="color:#777;padding-right:8px;white-space:nowrap">UTR / Ref No.</td><td style="font-weight:600;font-family:monospace;font-size:7pt">${txn.utr_number}</td></tr>` : ''}
          </table>
        </td>
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td style="width:50%;border-right:1px solid ${BORDER};padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Paid To (Supplier)</div>
          <div style="font-weight:700;font-size:9pt">${sup.name || '—'}</div>
          ${sup.address ? `<div style="font-size:7pt;color:#777">${sup.address}</div>` : ''}
          ${sup.phone ? `<div style="font-size:7.5pt;color:#555">Mob: ${sup.phone}</div>` : ''}
          ${sup.gst_number ? `<div style="font-size:6.5pt;color:#555">GSTIN: ${sup.gst_number}</div>` : ''}
          ${sup.drug_license_number ? `<div style="font-size:6.5pt;color:#555">DL No: ${sup.drug_license_number}</div>` : ''}
        </td>
        <td style="width:50%;padding:5px 8px;font-size:7.5pt;vertical-align:top">
          <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Against GRN</div>
          <div style="font-weight:700">${invoice.inward_no || '—'}</div>
          <div style="font-size:7.5pt;color:#777">GRN Date: ${fmtD(invoice.received_date)}</div>
          <div style="font-size:7.5pt;color:#777">GRN Amount: ₹${amountDue.toFixed(2)}</div>
          ${pay.payment_status ? `<div style="margin-top:2px"><span style="padding:1px 7px;border-radius:3px;font-size:6.5pt;font-weight:700;${pay.payment_status === 'PAID' ? 'background:#e8f5e9;color:#2e7d32' : pay.payment_status === 'PARTIAL' ? 'background:#fff3e0;color:#e65100' : 'background:#fdecea;color:#c62828'}">${pay.payment_status}</span></div>` : ''}
        </td>
      </tr>
      ${(pay.due_date || pay.next_payment_date) ? `
      <tr style="border-top:1px solid ${BORDER}">
        <td colspan="2" style="padding:4px 8px;font-size:7pt;color:#777">
          ${pay.due_date ? `<b>Due Date:</b> ${fmtD(pay.due_date)}` : ''}${pay.due_date && pay.next_payment_date ? ' &nbsp;|&nbsp; ' : ''}${pay.next_payment_date ? `<b>Next Payment Scheduled:</b> ${fmtD(pay.next_payment_date)}` : ''}
        </td>
      </tr>` : ''}
    </table>

    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;margin-top:0">
      <tr style="background:${LIGHT_BG}">
        <td style="${C}font-weight:700;font-size:8pt">This Payment</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:11pt;color:${PRIMARY}">₹${Number(txn.amount).toFixed(2)}</td>
      </tr>
    </table>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};border-top:none;font-size:7.5pt">
      <tr>
        <td style="padding:4px 7px;border-right:1px solid ${BORDER};font-weight:700;color:#555;white-space:nowrap;font-size:7pt">Amount (in words)</td>
        <td style="padding:4px 10px;font-style:italic">${amountInWords(Number(txn.amount))} Only</td>
      </tr>
    </table>
    ${txn.notes ? `<div style="margin-top:4px;padding:4px 8px;font-size:7.5pt;color:#666;font-style:italic;border:1px solid ${BORDER};border-radius:2px"><b>Notes:</b> ${txn.notes}</div>` : ''}

    <div style="margin-top:6px;font-size:7pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;letter-spacing:0.5px">Payment Schedule / History — this GRN</div>
    <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};margin-top:2px;table-layout:fixed">
      <tr>
        <th style="${CH}text-align:center;width:5%">#</th>
        <th style="${CH}text-align:left">Date</th>
        <th style="${CH}text-align:center">Mode</th>
        <th style="${CH}text-align:left">Reference</th>
        <th style="${CH}text-align:right">Amount (₹)</th>
        <th style="${CH}text-align:center">Status</th>
      </tr>
      ${historyRows || `<tr><td colspan="6" style="${C}text-align:center;color:#999">No prior payments</td></tr>`}
      <tr style="background:${LIGHT_BG}">
        <td colspan="4" style="${C}text-align:right;font-weight:700">Total Paid to Date</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:800">${totalPaid.toFixed(2)}</td>
        <td style="${C}"></td>
      </tr>
      <tr>
        <td colspan="4" style="${C}text-align:right;font-weight:700;color:${balanceDue > 0 ? '#c62828' : '#2e7d32'}">Balance Due</td>
        <td style="${C}text-align:right;font-family:monospace;font-weight:800;color:${balanceDue > 0 ? '#c62828' : '#2e7d32'}">${balanceDue.toFixed(2)}</td>
        <td style="${C}"></td>
      </tr>
    </table>

    <table style="width:100%;border-collapse:collapse;margin-top:5px;border:1px solid ${BORDER};font-size:7.5pt">
      <tr>
        <td style="width:70%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
          ${ph?.pan_no ? `<div><b>Company&apos;s PAN:</b> <span style="font-family:monospace;letter-spacing:0.8px">${ph.pan_no}</span></div>` : ''}
        </td>
        <td style="width:30%;vertical-align:bottom;text-align:center;padding:5px 7px;padding-top:16px">
          <div style="font-size:7.5pt;font-weight:700;color:#333;margin-bottom:16px">for ${ph?.name || ''}</div>
          <div style="border-top:1.5px solid #333;padding-top:4px;font-size:8pt;font-weight:700">Authorised Signatory</div>
        </td>
      </tr>
      <tr style="border-top:1px solid ${BORDER}">
        <td colspan="2" style="padding:4px 7px;text-align:center;vertical-align:middle;font-size:6.5pt;color:#aaa;font-style:italic">
          This is a Computer Generated Document
        </td>
      </tr>
    </table>
  `;

  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#00798E;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">Supplier Payment &mdash; ${invoice.inward_no || ''}${txn.utr_number ? ` / ${txn.utr_number}` : ''}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#00798E;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.print()" style="background:rgba(255,255,255,0.18);color:#fff;border:1px solid rgba(255,255,255,0.5);padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#8595; Download PDF</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>Supplier Payment Voucher ${invoice.inward_no || ''}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:10mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}<div class="pf-page" style="width:484px;margin:0 auto">${body}</div>
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>718)els[i].style.zoom=Math.max(0.55,718/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}
